import { DataGridConfig } from 'components/DataGrid';
import SummaryCard from 'components/SummaryCard';
import { useAtom, useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { truncateText } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import React, { Fragment, useEffect, useMemo, useState } from 'react';

import AgeAndStatusHistory from './AgeAndStatusHistory';
import { HostDetailModals } from './HostsDetailModals';
import RowActions from './RowActions';
import SeverityDescriptions from './SeverityDescriptions';
import {
  findingsDataQueryParamsAtom,
  orderbyAtom,
  paginationConfigAtom,
  selectedRowIdsAtom,
  sortOrderAtom,
  sourceTypeAtom,
  columnKeysEPAtom,
  columnKeysCAAtom,
  columnKeysSASTAtom,
  columnKeysWASAtom,
  columnKeysAllAtom,
  DEFAULT_COLUMN_KEYS_ALL,
  DEFAULT_COLUMN_KEYS_EP,
  DEFAULT_COLUMN_KEYS_CA,
  DEFAULT_COLUMN_KEYS_SAST,
  DEFAULT_COLUMN_KEYS_WAS,
  SourceType,
  sourceTypeConfig,
  findingsDataAtom,
  forceRerenderFindingGridAtom,
  isQuerystringFiltersInitializedAtom,
} from './state';

export const SeverityValueMapping = {
  5: 'Urgent',
  4: 'Critical',
  3: 'Serious',
  2: 'Medium',
  1: 'Minimal',
};

const SubscriptUtility = ({ label, subscriptText }) => (
  <>
    <span>{label}</span>
    <sub>
      <span>{subscriptText}</span>
    </sub>
  </>
);

const HostWithType = ({ rowData }) => {
  let sourceType = rowData.source_type;
  if (sourceType === SourceType.CA) {
    switch (rowData.ca_type) {
      case 'Workstations':
        sourceType = SourceType.CA_WORKSTATIONS;
        break;
      case 'Server':
        sourceType = SourceType.CA_SERVER;
        break;
    }
  } else if (sourceType === SourceType.EP) {
    switch (rowData.ep_scanner_type) {
      case 'External':
        sourceType = SourceType.EP_EXTERNAL;
        break;
      case 'Internal':
        sourceType = SourceType.EP_INTERNAL;
        break;
      case 'EC2':
        sourceType = SourceType.EP_EC2;
        break;
    }
  }

  const configData = sourceTypeConfig[sourceType] || sourceTypeConfig.default;
  const trimLength = 30;
  const trimName = (name, length = trimLength) => {
    if (!name) return '';
    if (name.length > length) {
      return name.substring(0, length) + '...';
    }
    return name;
  };

  const hostName = (rowData) => {
    if (rowData.source_type === SourceType.WAS) {
      return trimName(rowData.web_app_name);
    }
    if (rowData.source_type === SourceType.EP) {
      const alias = rowData['endpoint_alias'];
      const name = rowData['endpoint_ip'];
      const assetName = rowData['endpoint_asset_name'];
      const displayName = alias || assetName || name;

      return (
        <>
          <strong>{truncateText(displayName, 30)}</strong>
          {(alias || assetName) && (
            <div className="small">
              {alias && assetName
                ? truncateText(`${name} (${assetName})`, 27)
                : truncateText(name, 27)}
            </div>
          )}
        </>
      );
    }
    if (rowData.source_type === SourceType.CA) {
      const name = rowData['cloud_agent_name'];
      const alias = rowData['ca_alias'];
      const displayName = alias || name;

      return (
        <>
          <strong>{truncateText(displayName, 30)}</strong>
          {alias && <div className="small">{truncateText(name, 27)}</div>}
        </>
      );
    }
    if (rowData.source_type === SourceType.SAST) {
      return (
        <>
          <strong>{trimName(rowData.repository_name, 15)}</strong>/
          {trimName(rowData.branch, 15)}
        </>
      );
    }
  };

  return (
    <HostDetailModals
      rowData={rowData}
      hostNameRenderer={hostName}
      icon={configData.icon}
    />
  );
};

export const useFindingDataConfig = () => {
  const dataQueryParams = useAtomValue(findingsDataQueryParamsAtom);
  const isQuerystringFiltersInitialized = useAtomValue(
    isQuerystringFiltersInitializedAtom,
  );
  const [listingData, setListingData] = useAtom(findingsDataAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderby, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom); // Sort order
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);

  const sourceType = useAtomValue(sourceTypeAtom);
  const [columnKeysEP, setColumnKeysEP] = useAtom(columnKeysEPAtom);
  const [columnKeysCA, setColumnKeysCA] = useAtom(columnKeysCAAtom);
  const [columnKeysSAST, setColumnKeysSAST] = useAtom(columnKeysSASTAtom);
  const [columnKeysWAS, setColumnKeysWAS] = useAtom(columnKeysWASAtom);
  const [columnKeysAll, setColumnKeysAll] = useAtom(columnKeysAllAtom);

  const forceRenderGrid = useAtomValue(forceRerenderFindingGridAtom);

  const [loading, setLoading] = useState(false);

  const enableSelectionAcrossPages = true;

  useEffect(() => {
    // we are making the API call here, to get the loading screen on data grid
    // if we do it in atoms then the loading screen will not be shown
    setLoading(true);
    const fetchData = async () => {
      if (dataQueryParams === null) return;
      try {
        setListingData({ ...listingData, loading: true });
        const responseData = await callApi(
          `/findings/new/`,
          'GET',
          dataQueryParams,
        );
        setListingData({ ...responseData?.data, loading: false });
      } catch (error) {
        // eslint-disable-next-line no-debugger, @typescript-eslint/no-explicit-any
        const errorData = (error as any)?.data;
        const errorMessage = errorData
          ? `${Object.keys(errorData)[0]}: ${
              Object.values(errorData).flat()[0]
            }`
          : 'Failed to load data.';
        showFailureToast(errorMessage);
        setListingData({
          results: [],
          count: 0,
          loading: false,
          insights: {
            active: 0,
            new: 0,
            fixed: 0,
            reopened: 0,
            ignored: 0,
            urgent: 0,
            critical: 0,
            serious: 0,
            medium: 0,
            minimal: 0,
            assigned_to_me_count: 0,
            unique_assets: 0,
            watching_by_me_count: 0,
            unique_qids_or_rule_ids: 0,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    if (isQuerystringFiltersInitialized) {
      fetchData();
    }
  }, [dataQueryParams, isQuerystringFiltersInitialized]);

  const getColumnsDisplayConfig = () => {
    if (!sourceType)
      return {
        enabledColumnKeys: columnKeysAll,
        defaultColumnKeys: DEFAULT_COLUMN_KEYS_ALL,
        keysToBeExcluded: [
          'qid',
          'alias',
          'was_host',
          'ca_host',
          'sast_repo',
          'branch',
          'repository_name',
          'sonarqube_rule',
          'finding_id',
          'ca_type',
          'ca_alias',
          'ca_agent_key',
          'ca_agent_title',
          'ep_host',
          'endpoint_ip',
          'endpoint_name',
          'endpoint_alias',
          'ep_scanner_type',
          'fqdn',
          'port',
          'protocol',
          'ssl',
          'issue_type',
        ],
        onColumnsDisplayChange: (updatedColumnKeys) => {
          setColumnKeysAll(updatedColumnKeys);
        },
      };
    switch (true) {
      case [
        SourceType.EP,
        SourceType.EP_EXTERNAL,
        SourceType.EP_INTERNAL,
        SourceType.EP_EC2,
      ].includes(sourceType as SourceType):
        return {
          enabledColumnKeys: columnKeysEP,
          defaultColumnKeys: DEFAULT_COLUMN_KEYS_EP,
          keysToBeExcluded: [
            'default_host',
            'was_host',
            'finding_id',
            'ca_host',
            'ca_type',
            'ca_alias',
            'ca_agent_key',
            'ca_agent_title',
            'sast_repo',
            'branch',
            'repository_name',
            'sonarqube_rule',
            'issue_type',
          ],
          onColumnsDisplayChange: (updatedColumnKeys) => {
            setColumnKeysEP(updatedColumnKeys);
          },
        };
      case [
        SourceType.CA,
        SourceType.CA_SERVER,
        SourceType.CA_WORKSTATIONS,
      ].includes(sourceType as SourceType):
        return {
          enabledColumnKeys: columnKeysCA,
          defaultColumnKeys: DEFAULT_COLUMN_KEYS_CA,
          keysToBeExcluded: [
            'default_host',
            'was_host',
            'finding_id',
            'sast_repo',
            'branch',
            'repository_name',
            'sonarqube_rule',
            'ep_host',
            'endpoint_ip',
            'endpoint_name',
            'endpoint_alias',
            'ep_scanner_type',
            'issue_type',
          ],
          onColumnsDisplayChange: (updatedColumnKeys) => {
            setColumnKeysCA(updatedColumnKeys);
          },
        };
      case [SourceType.SAST].includes(sourceType as SourceType):
        return {
          enabledColumnKeys: columnKeysSAST,
          defaultColumnKeys: DEFAULT_COLUMN_KEYS_SAST,
          keysToBeExcluded: [
            'default_host',
            'was_host',
            'ep_host',
            'ca_host',
            'ca_type',
            'ca_alias',
            'ca_agent_key',
            'ca_agent_title',
            'endpoint_ip',
            'endpoint_name',
            'endpoint_alias',
            'ep_scanner_type',
            'finding_id',
            'qid',
            'fqdn',
            'port',
            'protocol',
            'ssl',
          ],
          onColumnsDisplayChange: (updatedColumnKeys) => {
            setColumnKeysSAST(updatedColumnKeys);
          },
        };
      case [SourceType.WAS].includes(sourceType as SourceType):
        return {
          enabledColumnKeys: columnKeysWAS,
          defaultColumnKeys: DEFAULT_COLUMN_KEYS_WAS,
          keysToBeExcluded: [
            'default_host',
            'ca_host',
            'ca_type',
            'ca_alias',
            'ca_agent_key',
            'ca_agent_title',
            'ep_host',
            'endpoint_ip',
            'default_host',
            'endpoint_name',
            'endpoint_alias',
            'ep_scanner_type',
            'sast_repo',
            'branch',
            'repository_name',
            'sonarqube_rule',
            'fqdn',
            'issue_type',
          ],
          onColumnsDisplayChange: (updatedColumnKeys) => {
            setColumnKeysWAS(updatedColumnKeys);
          },
        };
    }
  };

  const config: DataGridConfig = useMemo(
    () => ({
      rowIdKey: 'id',
      columns: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
          headerTooltip: 'Unique Identifier for the Finding in SP360 Database',
          enableCopyToClipboard: true,
        },
        {
          key: 'default_host',
          label: 'Host Asset',
          headerTooltip: 'Host Information',
          overrideContents: (rowData) => <HostWithType rowData={rowData} />,
          tooltip: (rowData) => {
            if (rowData.source_type === SourceType.WAS) {
              return (
                <>
                  <strong>Web Application:</strong>&nbsp;&nbsp;
                  {rowData.web_app_name}
                  <br />
                  <strong>URL: </strong>
                  {rowData.web_app ? JSON.parse(rowData.web_app).url : ''}
                  <br />
                  <br />
                  Click to view web application details
                </>
              );
            }
            if (rowData.source_type === SourceType.EP) {
              const alias = rowData['endpoint_alias'];
              const name = rowData['endpoint_ip'];
              const assetName = rowData['endpoint_asset_name'];

              return (
                <>
                  <>
                    IP Address: {name} <br />
                    {alias && (
                      <>
                        Alias: {alias} <br />
                      </>
                    )}
                    {assetName && (
                      <>
                        Asset Name: {assetName} <br />
                      </>
                    )}
                    <br />
                    <br />
                    Click to view endpoint details
                  </>
                </>
              );
            }
            if (rowData.source_type === SourceType.CA) {
              const name = rowData['cloud_agent_name'];
              const alias = rowData['ca_alias'];
              return (
                <>
                  Cloud Agent: {name}
                  <br />
                  {alias && (
                    <>
                      Alias: {alias}
                      <br />
                    </>
                  )}
                  <br />
                  <br />
                  Click to view cloud agent details
                </>
              );
            }
            if (rowData.source_type === SourceType.SAST) {
              return (
                <>
                  Repository: {rowData.repository_name}
                  <br /> Branch: {rowData.branch}
                  <br />
                  <br />
                  Click to view repository details
                </>
              );
            }
          },
        },
        {
          key: 'was_host',
          label: 'Web Application',
          sortable: true,
          headerTooltip: 'Web Application information',
          overrideContents: (rowData) => <HostWithType rowData={rowData} />,
          tooltip: (rowData) => (
            <Fragment>
              <strong>Web Application:</strong>&nbsp;&nbsp;
              {rowData['web_app_name']}
              <br />
              <strong>URL: </strong>
              {rowData['web_app'] ? JSON.parse(rowData['web_app']).url : ''}
              <br />
              <br />
              Click to view web application details
            </Fragment>
          ),
          valueToCopy: (rowData) => rowData.web_app_name,
          orderBy: 'web_app_name',
        },
        {
          key: 'ca_host',
          label: 'Cloud Agent',
          sortable: true,
          headerTooltip: 'Respective Cloud Agent information',
          overrideContents: (rowData) => <HostWithType rowData={rowData} />,
          tooltip: (rowData) => {
            const name = rowData['cloud_agent_name'];
            const alias = rowData['ca_alias'];
            return (
              <>
                Cloud Agent: {name}
                <br />
                {alias && (
                  <>
                    Alias: {alias}
                    <br />
                  </>
                )}
                <br />
                <br />
                Click to view cloud agent details
              </>
            );
          },
          valueToCopy: (rowData) => rowData.cloud_agent_name,
          orderBy: 'cloud_agent_name',
        },
        {
          key: 'ca_agent_key',
          label: 'Agent Key',
          sortable: true,
          headerTooltip: 'Cloud Agent activation key',
          enableCopyToClipboard: true,
        },
        {
          key: 'ca_agent_title',
          label: 'Agent Key Title',
          sortable: true,
          headerTooltip: 'Cloud Agent activation key title',
          overrideContents: (rowData) => (
            <Fragment>
              {rowData['ca_agent_title']
                ? rowData['ca_agent_title'].split('-')[1].trim()
                : ''}
            </Fragment>
          ),
          tooltip: (rowData) => (
            <Fragment>
              {rowData['ca_agent_title']
                ? rowData['ca_agent_title'].split('-')[1].trim()
                : ''}
            </Fragment>
          ),
        },
        {
          key: 'ca_alias',
          label: 'Alias',
          sortable: true,
          headerTooltip: 'Cloud Agent Alias',
        },
        {
          key: 'sast_repo',
          label: 'Repository / Branch',
          sortable: true,
          headerTooltip: 'Respective Repository Branch information',
          overrideContents: (rowData) => <HostWithType rowData={rowData} />,
          tooltip: (rowData) => (
            <>
              Repository: {rowData.repository_name}
              <br /> Branch: {rowData.branch}
              <br />
              <br />
              Click to view repository details
            </>
          ),
          valueToCopy: (rowData) =>
            `${rowData.repository_name}/${rowData.branch}`,
          orderBy: 'repository_name',
        },
        {
          key: 'repository_name',
          label: 'Repository',
          sortable: true,
          headerTooltip: 'Repository Name',
        },
        {
          key: 'branch',
          label: 'Branch',
          sortable: true,
          headerTooltip: 'Branch Name',
        },
        {
          key: 'sonarqube_rule',
          label: 'Rule ID',
          sortable: true,
          headerTooltip: 'SonarQube Rule ID for the Vulnerability',
          enableCopyToClipboard: true,
        },
        {
          key: 'qid',
          label: 'QID',
          sortable: true,
          headerTooltip: 'Qualys Vulnerability ID',
          enableCopyToClipboard: true,
        },
        {
          key: 'finding_id',
          label: 'Finding ID',
          headerTooltip: "Web Application Finding's Qualys ID",
          enableCopyToClipboard: true,
        },
        {
          key: 'ca_type',
          label: 'Type',
          overrideContents: (rowData) =>
            rowData.ca_type === 'Workstations' ? 'Workstation' : 'Server',
          sortable: true,
          headerTooltip: 'Cloud Agent Type. Eg: Workstation, Server etc.',
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          // NOTE: auto width for one column to expand automatically
          headerCellStyle: { width: 'auto' },
          rowCellStyle: { width: 'auto' },
          headerTooltip: 'Vulnerability Name',
          enableCopyToClipboard: true,
          maxCharLength: 60,
        },
        {
          key: 'ep_host',
          label: 'Endpoint',
          headerTooltip: 'Respective Endpoint information',
          overrideContents: (rowData) => <HostWithType rowData={rowData} />,
          tooltip: (rowData) => {
            const name = rowData['endpoint_ip'];
            const alias = rowData['endpoint_alias'];
            const assetName = rowData['endpoint_asset_name'];
            return (
              <>
                IP Address: {name} <br />
                {alias && (
                  <>
                    Alias: {alias} <br />
                  </>
                )}
                {assetName && (
                  <>
                    Asset Name: {assetName} <br />
                  </>
                )}
                <br />
                <br />
                Click to view endpoint details
              </>
            );
          },
          sortable: true,
          orderBy: 'endpoint_ip',
          valueToCopy: (rowData) => rowData.endpoint_ip,
        },
        {
          key: 'endpoint_ip',
          label: 'Endpoint IP',
          sortable: true,
          orderBy: 'ep_big_ip',
          headerTooltip: 'Respective Endpoint IP Address',
          enableCopyToClipboard: true,
        },
        {
          key: 'endpoint_alias',
          label: 'Alias',
          sortable: true,
        },
        {
          key: 'ep_scanner_type',
          label: 'Endpoint Type',
          sortable: true,
          headerTooltip: 'Type of Endpoint. Eg: External, Internal, AWS etc.',
          overrideContents: (rowData) =>
            rowData.ep_scanner_type === 'EC2' ? 'AWS' : rowData.ep_scanner_type,
        },
        {
          key: 'severity',
          label: 'Severity',
          sortable: true,
          overrideContents: (rowData) => SeverityValueMapping[rowData.severity],
          classRules: [
            {
              className: 'text-urgent',
              ruleFunction: (rowData) => rowData.severity === 5,
            },
            {
              className: 'text-critical',
              ruleFunction: (rowData) => rowData.severity === 4,
            },
            {
              className: 'text-serious',
              ruleFunction: (rowData) => rowData.severity === 3,
            },
            {
              className: 'text-medium',
              ruleFunction: (rowData) => rowData.severity === 2,
            },
            {
              className: 'text-minimal',
              ruleFunction: (rowData) => rowData.severity === 1,
            },
          ],
          headerTooltip: <SeverityDescriptions />,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          orderBy: 'status,is_ignored',
          tooltip: (_rowData) =>
            getStatusTooltip(_rowData.status, _rowData.is_ignored),
          overrideContents: (rowData) => (
            <>
              {rowData.is_ignored ? (
                <SubscriptUtility
                  label="IGNORED"
                  subscriptText={rowData.status}
                />
              ) : (
                <text>{rowData.status}</text>
              )}
            </>
          ),
          headerTooltip: 'Vulnerability Status',
        },
        {
          key: 'is_ignored',
          label: 'Ignored',
          sortable: true,
        },
        {
          key: 'ignored_by',
          label: 'Ignored By',
          sortable: true,
        },
        {
          key: 'ignored_date',
          label: 'Ignored Date',
          sortable: true,
        },
        {
          key: 'unignored_by',
          label: 'Unignored By',
          sortable: true,
        },
        {
          key: 'unignored_date',
          label: 'Unignored Date',
          sortable: true,
        },
        {
          key: 'last_detected_date',
          label: 'Last Detected Date',
          sortable: true,
          classRules: [
            {
              className: 'text-critical',
              ruleFunction: (rowData) => {
                const lastDetectedDate = new Date(rowData.last_detected_date);
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - lastDetectedDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 10;
              },
            },
            {
              className: 'text-medium',
              ruleFunction: (rowData) => {
                const lastDetectedDate = new Date(rowData.last_detected_date);
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - lastDetectedDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 7 && daysDifference <= 10;
              },
            },
          ],
          tooltip: (rowData) => {
            const lastDetectedDate = new Date(rowData.last_detected_date);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - lastDetectedDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `Last detected ${daysDifference} days ago`
              : 'Last detected today';
          },
        },
        {
          key: 'last_tested_date',
          label: 'Last Tested Date',
          sortable: true,
          classRules: [
            {
              className: 'text-critical',
              ruleFunction: (rowData) => {
                const lastTestedDate = new Date(rowData.last_tested_date);
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - lastTestedDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 10;
              },
            },
            {
              className: 'text-medium',
              ruleFunction: (rowData) => {
                const lastTestedDate = new Date(rowData.last_tested_date);
                const currentDate = new Date();
                const timeDifference =
                  currentDate.getTime() - lastTestedDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference > 7 && daysDifference <= 10;
              },
            },
          ],
          tooltip: (rowData) => {
            const lastTestedDate = new Date(rowData.last_tested_date);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - lastTestedDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `Last tested ${daysDifference} days ago`
              : 'Last tested today';
          },
        },
        {
          key: 'due_date',
          label: 'Due Date',
          sortable: true,
          overrideContents: (rowData) =>
            rowData.due_date
              ? new Date(rowData.due_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : '',
          classRules: [
            {
              className: 'text-urgent',
              ruleFunction: (rowData) =>
                new Date(rowData.due_date) < new Date(),
            },
            {
              className: 'text-serious',
              ruleFunction: (rowData) => {
                const dueDate = new Date(rowData.due_date);
                const currentDate = new Date();
                const timeDifference =
                  dueDate.getTime() - currentDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference <= 10 && daysDifference >= 0;
              },
            },
            {
              className: 'text-medium',
              ruleFunction: (rowData) => {
                const dueDate = new Date(rowData.due_date);
                const currentDate = new Date();
                const timeDifference =
                  dueDate.getTime() - currentDate.getTime();
                const daysDifference = timeDifference / (1000 * 3600 * 24);
                return daysDifference <= 20 && daysDifference > 10;
              },
            },
          ],
          tooltip: (rowData) => {
            const dueDate = new Date(rowData.due_date);
            const currentDate = new Date();
            const timeDifference = dueDate.getTime() - currentDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            const msg =
              daysDifference > 0
                ? `Due in ${daysDifference} days`
                : 'Due date passed';
            return (
              <>
                <strong>Due Date: </strong>
                {new Date(rowData.due_date)?.toLocaleString()}
                <br />
                {msg}
              </>
            );
          },
        },
        {
          key: 'age',
          label: 'Age',
          sortable: true,
          headerTooltip: 'Vulnerability Age in Days',
          overrideContents: (rowData) => (
            <AgeAndStatusHistory rowData={rowData} />
          ),
          tooltip: () => "Click to view the vulnerability's status history",
        },
        {
          key: 'assigned_member_label',
          label: 'Assigned To',
          sortable: true,
          headerTooltip: 'Assigned Team Member',
          tooltip: (rowData) => (
            <SummaryCard
              heading={`${rowData.assigned_member_label}`}
              data={[
                { key: 'Email', value: rowData.assigned_member_email },
                { key: 'Id', value: rowData.assigned_member_id },
              ]}
              description={'Assigned user for this vulnerability in SP360'}
              icon={
                <i
                  className="fa fa-user"
                  aria-hidden="true"
                  style={{ fontSize: '3rem' }}
                ></i>
              }
              mode="tooltip"
            />
          ),
        },
        {
          key: 'fqdn',
          label: 'FQDN',
          sortable: true,
          headerTooltip: 'Fully Qualified Domain Name',
        },
        {
          key: 'port',
          label: 'Port',
          sortable: true,
          headerTooltip: 'Port Number',
        },
        {
          key: 'protocol',
          label: 'Protocol',
          sortable: true,
          headerTooltip: 'Network Protocol',
        },
        {
          key: 'ssl',
          label: 'SSL',
          sortable: true,
          headerTooltip: 'SSL Enabled',
        },
        {
          key: 'issue_type',
          label: 'Issue Type',
          sortable: true,
          headerTooltip: 'Type of Issue. Eg: Vulnerability, Bug, etc.',
        },
        {
          key: 'notes_count',
          label: 'Notes Count',
          sortable: true,
          headerTooltip: 'Number of Notes',
        },
        {
          key: 'watcher_count',
          label: 'Watchers Count',
          sortable: true,
          headerTooltip: 'Number of Watchers',
        },
        {
          key: 'actions',
          label: 'Actions',
          render: (rowData) => <RowActions rowData={rowData} />,
          headerTooltip: 'Available Actions',
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
            whiteSpace: 'nowrap',
          },
        },
      ],
      columnsDisplayConfig: getColumnsDisplayConfig(),
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1, // page number expects a zero based index
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          // console.log("page number changed to " + newPage);
          // page number returns a zero based index, so incrementing by 1
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });

          if (!enableSelectionAcrossPages) {
            // reset selected row ids on change
            setSelectedRowIds([]);
          }
        },
        onPageSizeChange: (newPageSize: number) => {
          // console.log("page size changed to " + newPageSize);
          // reset page number on page size change
          setPaginationConfig({ page_size: newPageSize, page: 1 });

          // reset selected row ids on change
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderby,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },
      rowSelectionConfig: {
        enableSelectionAcrossPages: enableSelectionAcrossPages,
        preSelectedRows: selectedRowIds,
        onSelectionChange: (updatedRowIds) => {
          setSelectedRowIds(updatedRowIds);
        },
      },
    }),
    [
      listingData,
      columnKeysAll,
      columnKeysCA,
      columnKeysEP,
      columnKeysSAST,
      columnKeysWAS,
      orderby,
      sortOrder,
      paginationConfig,
      selectedRowIds,
      forceRenderGrid,
    ],
  );

  return { config, data: listingData?.results, loading };
};

function getStatusTooltip(status: string, isIgnored): string {
  if (isIgnored) {
    return 'The vulnerability is resolved by marking it IGNORED.';
  }
  switch (status) {
    case 'ACTIVE':
      return 'The vulnerability status is ACTIVE and detected more then once.';
    case 'NEW':
      return 'The vulnerability status is NEW, and recently got detected in last scan.';
    case 'REOPENED':
      return 'The vulnerability status in REOPENED, but has been addressed, but has been recently re-detected in last scan.';
    case 'FIXED':
      return 'The vulnerability status is FIXED. Its been resolved.';
    default:
      return status;
  }
}
